import { Fragment } from 'react';
import { AllocationListField } from '../fields/allocation_list_field.js';
import type { UseMultiTravelerCheckoutFormResult } from '../form/use_multi_traveler_checkout_form.js';
import type { CheckoutCopy } from '../model/checkout_copy.js';
import type { CheckoutIo } from '../model/checkout_env.js';
import type { CheckoutField, CheckoutTravelerRef } from '../model/checkout_source.js';
import type { CheckoutAllocationOwner } from '../model/product_contract.js';
import { CheckoutMotiveSection } from '../motive/checkout_motive_section.js';
import { BudgetLighthouse } from '../traveler/budget_lighthouse.js';
import type { CheckoutTravelerAllocation } from '../traveler/checkout_traveler_card.js';
import { budgetLighthouse } from './budget_lighthouse.js';
import { useCheckoutEnv } from './env_context.js';
import { TravelerCard, CorporateFieldItem, costCenterNameOf } from './form_host.js';

export interface MultiTravelerCheckoutFormProps {
  fields: CheckoutField[];
  state: UseMultiTravelerCheckoutFormResult;
  requesterId?: string;
  withinPolicy: boolean;
  /** Total product-aware do carrinho — o farol compara com o saldo devolvido. */
  total: number;
  /** `'requester'` (multi de hotel): só o bloco dono do rateio aloca (o
   *  requester, ou o hóspede da política numa reserva só para terceiros) — os
   *  outros não veem o campo de alocação nem o erro de catálogo vazio.
   *  Default `'every-traveler'` (voo). */
  allocationOwner?: CheckoutAllocationOwner;
  /** Texto opcional exibido no lugar do campo de alocação para o acompanhante
   *  quando `allocationOwner === 'requester'` e o dono é o requester. Numa
   *  reserva só para terceiros o texto nomeia o hóspede dono do rateio.
   *  Ausente ⇒ nada é renderizado. */
  allocationHint?: string;
  /** O aviso de fora da política com os motivos (#5262, `outOfPolicyReasons`).
   *  Ausente ⇒ o aviso genérico. */
  outOfPolicyHint?: string;
  /** O título da seção do motivo; ausente, o da viagem. */
  motiveTitle?: string;
}

type TravelerSlice = UseMultiTravelerCheckoutFormResult['travelers'][number];

interface AllocationContext {
  state: UseMultiTravelerCheckoutFormResult;
  index: number;
  ownsAllocation: boolean;
  total: number;
  allocationOwner: CheckoutAllocationOwner;
  allocationHint: string | undefined;
  copy: CheckoutCopy;
  searchCostCenters: CheckoutIo['searchCostCenters'];
  locale: string;
}

/** O rateio do cartão do viajante. Ausente enquanto o form dele carrega. */
function travelerAllocation(
  slice: TravelerSlice,
  ctx: AllocationContext,
): CheckoutTravelerAllocation | undefined {
  const { form } = slice;
  if (!form) return undefined;
  // Acompanhante em reserva de hotel: o rateio é do dono (solicitante ou
  // hóspede da política).
  if (ctx.allocationOwner === 'requester' && !ctx.ownsAllocation) {
    return { kind: 'from-requester', ...(ctx.allocationHint ? { hint: ctx.allocationHint } : {}) };
  }
  // Colega sem nenhuma empresa/centro de custo corporativo: não há o que alocar.
  if (form.companies.length === 0) {
    return { kind: 'unavailable', message: ctx.copy.traveler.noCorporateAccess };
  }
  // Farol junto do rateio que ele reflete: só no cartão do bloco cujo saldo o
  // form consultou (o dono, ou o único viajante). Com vários pagando, nenhum.
  // Num rateio de várias linhas, um farol por linha, pela parte dela (#6697).
  const budgeted = slice.traveler.id === ctx.state.budgetTravelerId;
  const rows = slice.allocations;
  const lighthouseOf = (index: number) => {
    const row = rows[index];
    if (!row) return undefined;
    const share = rows.length > 1 ? (ctx.total * (row.percent ?? 0)) / 100 : ctx.total;
    return budgetLighthouse(
      ctx.state.budgets?.[index],
      share,
      costCenterNameOf(form, row),
      ctx.copy.budget,
      ctx.locale,
    );
  };
  const lighthouse = budgeted && rows.length === 1 ? lighthouseOf(0) : undefined;
  const rowFooters =
    budgeted && rows.length > 1
      ? rows.map((_, index) => {
          const props = lighthouseOf(index);
          return props ? (
            // eslint-disable-next-line react/no-array-index-key -- one lighthouse per allocation row, which is its position
            <BudgetLighthouse key={index} {...props} />
          ) : null;
        })
      : undefined;
  return {
    kind: 'field',
    field: (
      <AllocationListField
        catalog={form}
        value={rows}
        allowMany={form.allowManyAllocations === true}
        searchCostCenters={ctx.searchCostCenters}
        copy={ctx.copy.fields.allocation}
        splitCopy={ctx.copy.fields.allocationSplit}
        projectRequired
        rowFooters={rowFooters}
        onChange={(next) => {
          ctx.state.updateAllocations(ctx.index, next);
        }}
      />
    ),
    ...(lighthouse ? { lighthouse: <BudgetLighthouse {...lighthouse} /> } : {}),
  };
}

/** O motivo, a justificativa e a observação, coletados UMA vez para o pedido,
 *  e — com mais de um viajante — o dono da política. */
function BookingFields({
  fields,
  form,
  state,
  withinPolicy,
  title,
  outOfPolicyHint,
  policyTraveler,
}: {
  readonly fields: readonly CheckoutField[];
  readonly form: NonNullable<TravelerSlice['form']>;
  readonly state: UseMultiTravelerCheckoutFormResult;
  readonly withinPolicy: boolean;
  readonly title: string;
  readonly outOfPolicyHint: string | undefined;
  readonly policyTraveler: CheckoutTravelerRef | undefined;
}) {
  const { traveler: labels } = useCheckoutEnv().copy;
  return (
    <CheckoutMotiveSection
      title={title}
      {...(withinPolicy ? {} : { outOfPolicyHint: outOfPolicyHint ?? labels.outOfPolicy })}
      {...(policyTraveler
        ? { policyOwner: { label: labels.policyLabel, name: policyTraveler.name } }
        : {})}
    >
      {fields.map((field) => (
        <Fragment key={field.kind === 'select' ? field.id : field.kind}>
          <CorporateFieldItem
            field={field}
            form={form}
            withinPolicy={withinPolicy}
            motiveId={state.motiveId}
            justificationId={state.justificationId}
            observation={state.observation}
            onMotiveChange={state.setMotiveId}
            onJustificationChange={state.setJustificationId}
            onObservationChange={state.setObservation}
          />
        </Fragment>
      ))}
    </CheckoutMotiveSection>
  );
}

/** A coluna principal do checkout: um cartão por viajante (identidade + rateio) e,
 *  abaixo, o motivo/justificativa/observação coletados UMA vez e, com mais de um
 *  viajante, o dono da política. Estado e envio moram no drawer
 *  (`useMultiTravelerCheckoutForm`); aqui só composição. */
export function MultiTravelerCheckoutForm({
  fields,
  state,
  requesterId,
  withinPolicy,
  total,
  allocationOwner = 'every-traveler',
  allocationHint,
  outOfPolicyHint,
  motiveTitle,
}: Readonly<MultiTravelerCheckoutFormProps>) {
  const { io, copy, locale } = useCheckoutEnv();
  const searchCostCenters = io?.searchCostCenters;
  const travelerFields = fields.filter((f) => f.group === 'traveler');
  // Campos da reserva = os corporativos menos o rateio (que é por viajante).
  const bookingFields = fields.filter((f) => f.group === 'corporate' && f.kind !== 'allocation');
  const firstForm = state.travelers[0]?.form;
  const multiple = state.travelers.length > 1;
  const policyTraveler = state.travelers.find(
    ({ traveler }) => traveler.id === state.policyTravelerId,
  )?.traveler;
  // O acompanhante leva o rateio do titular do quarto dele (#5522): o nome do
  // titular, ou o texto do host quando o titular é quem reserva.
  const payerHint = (travelerId: string): string | undefined => {
    const holderId = state.rooms.find((room) => room.includes(travelerId))?.[0];
    const holder = state.travelers.find(({ traveler }) => traveler.id === holderId);
    return holder && holder.traveler.id !== requesterId
      ? copy.traveler.allocationFromGuest(holder.form?.traveler?.name ?? holder.traveler.name)
      : allocationHint;
  };

  return (
    <>
      {state.travelers.map((slice, index) => {
        const isRequester = Boolean(requesterId) && slice.traveler.id === requesterId;
        const allocation = travelerAllocation(slice, {
          state,
          index,
          ownsAllocation: state.rooms.some((room) => room[0] === slice.traveler.id),
          total,
          allocationOwner,
          allocationHint: payerHint(slice.traveler.id),
          copy,
          searchCostCenters,
          locale,
        });
        return (
          <TravelerCard
            key={slice.traveler.id}
            fields={travelerFields}
            traveler={slice.form?.traveler}
            editing={slice.travelerEditing}
            travelerDraft={slice.travelerDraft}
            idPrefix={`t${String(index)}-`}
            fallbackName={slice.traveler.name}
            isRequester={isRequester}
            {...(allocation ? { allocation } : {})}
            onToggleEditing={(v) => {
              state.setTravelerEditing(index, v);
            }}
            onUpdateTraveler={(patch) => {
              state.updateTraveler(index, patch);
            }}
          />
        );
      })}

      {firstForm && (
        <BookingFields
          fields={bookingFields}
          form={firstForm}
          state={state}
          withinPolicy={withinPolicy}
          title={motiveTitle ?? copy.motive.trip.title}
          outOfPolicyHint={outOfPolicyHint}
          policyTraveler={multiple ? policyTraveler : undefined}
        />
      )}
    </>
  );
}
